/**
 * Centralized date formatting helpers.
 *
 * API dates  — always use a fixed English / ISO format so they are never
 *              affected by the user's UI language selection.
 * Display dates — use the locale from the LanguageProvider.
 *
 * IMPORTANT: Never pass a dynamic `locale` value into functions that produce
 * values sent to the backend or external APIs.
 */

// ── API formatting (locale-independent, always English / ISO) ──────────────

/** Fixed locale used whenever a date or number needs to be serialised for an
 *  API call. This must NEVER depend on the user's language selection. */
export const API_LOCALE = "en-US" as const;

/**
 * Format a Date (or date string) as YYYY-MM-DD for API consumption.
 *
 * Uses **local** date components so that dates created via
 * `new Date(year, month, day)` (local midnight) are not shifted to the
 * previous day when the user's timezone is ahead of UTC.
 */
export function formatDateForApi(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Parse a "YYYY-MM-DD" date string as **local** midnight.
 *
 * `new Date("2026-03-01")` is interpreted as UTC midnight by the spec, which
 * can land on the previous local day in UTC+ timezones.  This helper avoids
 * that pitfall by splitting the string and using the `(year, month, day)`
 * constructor which always produces local midnight.
 */
export function parseLocalDate(dateStr: string): Date {
  // Tolerate full ISO timestamps (e.g. Postgres DATE columns serialized as
  // "2026-07-14T00:00:00.000Z") by using only the leading YYYY-MM-DD part, so
  // the time/offset can never turn the day component into NaN (Invalid Date).
  const [y, m, d] = dateStr.slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d);
}

// ── Display formatting helpers ─────────────────────────────────────────────

/**
 * Format a date for display using the supplied UI locale.
 * This is the ONLY path where the dynamic locale should be used.
 *
 * Date strings ("YYYY-MM-DD") are parsed as local midnight via
 * `parseLocalDate` to avoid the UTC-shift pitfall.
 */
export function formatDateForDisplay(
  date: Date | string,
  locale: string,
  options?: Intl.DateTimeFormatOptions,
): string {
  const d = typeof date === "string" ? parseLocalDate(date) : date;
  return d.toLocaleDateString(locale, options);
}

/**
 * Format a date-time for display using the supplied UI locale.
 */
export function formatDateTimeForDisplay(
  date: Date | string,
  locale: string,
  options?: Intl.DateTimeFormatOptions,
): string {
  // Date-time strings (ISO 8601 with time component) must be parsed by the
  // native Date constructor so the time and timezone are honoured. Bare
  // calendar strings ("YYYY-MM-DD") still go through `parseLocalDate` to
  // avoid the UTC-shift pitfall.
  let d: Date;
  if (typeof date === "string") {
    d = /[T\s]/.test(date) ? new Date(date) : parseLocalDate(date);
  } else {
    d = date;
  }
  return d.toLocaleString(locale, options);
}

/**
 * A distance in time, in words, in the UI locale — "12 min. ago", "in 18
 * min.", "2 days ago".
 *
 * The sidebar's sync indicator says when the last check ran this way because
 * a clock time asks the reader to subtract. The unit steps up as the distance
 * grows, so a stale run reads as days rather than as thousands of minutes;
 * inside the first minute it says "now" rather than counting seconds that
 * are wrong by the time they are read. An em dash for a value it cannot
 * parse, the same blank the status tiles use.
 */
export function formatRelativeTime(
  value: Date | string,
  locale: string,
  now: Date = new Date(),
): string {
  const target = typeof value === "string" ? new Date(value) : value;
  const diffMs = target.getTime() - now.getTime();
  if (Number.isNaN(diffMs)) return "—";

  const format = new Intl.RelativeTimeFormat(locale, { numeric: "auto", style: "short" });
  const abs = Math.abs(diffMs);
  if (abs < 45_000) return format.format(0, "second");
  const minutes = diffMs / 60_000;
  if (abs < 59.5 * 60_000) return format.format(Math.round(minutes), "minute");
  const hours = minutes / 60;
  if (abs < 23.5 * 3_600_000) return format.format(Math.round(hours), "hour");
  return format.format(Math.round(hours / 24), "day");
}
